import React, { useState, useCallback } from "react";
import { createSelector } from "reselect";
import { useSelector, useDispatch } from "react-redux";
import { invalidateItemAndChildren } from "../../actions/flowActions";
import { flowItemSetOfferCode, flowSetClientVariable } from "../../actions/clientVariableActions";
import { arrayMoveImmutable } from "array-move";
import { FlowItemClientVariableD } from "../../types/stores/flowTypes";
import { IAppState } from "../../types/stores";
import { VariableValueType, ClientVariableKind, ClientVariableWithValue } from "../../types/stores/vars";
import { getFlowItemClientVariablesForSelectedFlow } from "../../reducers/flowItemClientVariables";

import TableHeader from "../material-components/Table/TableHeader";
import TableBody from "../material-components/Table/TableBody";
import TableFooter from "../material-components/Table/TableFooter";
import { stableSort, getSorting } from "../../helpers/materialTableFunctions";

import Paper from "@material-ui/core/Paper";
import Table from "@material-ui/core/Table";
import MaterialTooltip from "@material-ui/core/Tooltip";
import Checkbox from "@material-ui/core/Checkbox";
import NumericalTextField from "../material-components/Misc/NumericalTextField";

//This is forceUpdate built in react-hooks
export function useForceUpdate(): () => void {
    const [, setTick] = useState(0);
    const update = useCallback(() => {
        setTick(tick => tick + 1);
    }, []);
    return update;
}

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const makeGetFlowOfferCodesWithValues = () =>
    //ts-expect-error
    createSelector(
        (state: IAppState) => getFlowItemClientVariablesForSelectedFlow(state),
        (flowItemClientVariables: Array<FlowItemClientVariableD>): Array<number> => {
            const variablesWithValuesAssigned = flowItemClientVariables.filter(
                x => x.VariableValue.ValueString || x.VariableValue.FieldId
            );
            const uniqueVariableIds = [...new Set(variablesWithValuesAssigned.map(x => x.VariableId))];
            return uniqueVariableIds;
        }
    );

interface Props {
    clientVariablesWithValues: Array<ClientVariableWithValue>;
    variableScope: string;
}

const FlowVisibleVariablesEdit: React.FC<Props> = ({ clientVariablesWithValues, variableScope }: Props) => {
    const dispatch = useDispatch();
    const forceUpdate = useForceUpdate();
    //State
    const [order, setOrder] = useState("asc");
    const [orderBy, setOrderBy] = useState("SortOrder");
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const getOfferCodesWithValues = makeGetFlowOfferCodesWithValues();

    //Redux
    const selectedFlow = useSelector((state: IAppState) => state.selected.flow);
    const selectedFlowItem = useSelector((state: IAppState) => state.selected.flowItem);
    const offerCodesWithValues = useSelector<IAppState, Array<number>>(state => getOfferCodesWithValues(state));

    const invalidate = () => dispatch(invalidateItemAndChildren(selectedFlowItem));

    const updateItemOfferCode = (variableId: number, isVisible: boolean, sortOrder: number | null) =>
        dispatch(flowItemSetOfferCode(selectedFlow, selectedFlowItem, variableId, isVisible, sortOrder));

    const checkboxCell = (cellInfo: ClientVariableWithValue) => {
        const currRow = clientVariablesWithValues.find(x => x.Id == cellInfo.Id);
        if (!currRow) {
            return null;
        }

        let valueAssigned = false;
        if (variableScope == "CampaignFlow") {
            const variableValue: VariableValueType | null = currRow.VariableValue;
            valueAssigned = variableValue != null && Boolean(variableValue.ValueString || variableValue.FieldId);
        } else {
            valueAssigned = offerCodesWithValues.includes(cellInfo.Id);
        }

        let visibleCheckBox = (
            <Checkbox
                id={"is-visible-" + currRow.Id}
                icon={
                    <i className="material-icons" style={{ fontSize: 16 }}>
                        check_box_outline_blank
                    </i>
                }
                checkedIcon={
                    <i className="material-icons" style={{ fontSize: 16 }}>
                        check_box
                    </i>
                }
                style={{ color: "#406eb3", height: 24, width: 24 }}
                color="primary"
                checked={currRow.IsVisible}
                value={currRow.IsVisible}
                disabled={valueAssigned}
                onClick={() => {
                    currRow.IsVisible = !currRow.IsVisible;
                    updateClientVariable(currRow);
                    forceUpdate();
                }}
            />
        );

        if (valueAssigned) {
            visibleCheckBox = (
                <MaterialTooltip title={"Hiding not allowed when values are assigned"}>
                    <div>{visibleCheckBox}</div>
                </MaterialTooltip>
            );
        }

        return visibleCheckBox;
    };

    const numberInputCell = (cellInfo: ClientVariableWithValue) => {
        const currRow = clientVariablesWithValues.find(x => x.Id == cellInfo.Id);
        if (!currRow || !currRow.IsVisible) {
            return null;
        }

        return (
            <NumericalTextField
                className="material-form-control"
                value={currRow.SortOrder}
                onBlur={e => {
                    let value = parseInt(e.target.value);
                    if (value < 1) {
                        value = 1;
                    }
                    onSortOrderChange(value.toString(), currRow.Id);
                }}
            />
        );
    };

    const labelCell = (value: string) => (
        <MaterialTooltip title={value}>
            <span>{value}</span>
        </MaterialTooltip>
    );

    const onSortOrderChange = (val: string, Id: number) => {
        const visibleOffers = clientVariablesWithValues.filter(x => x.IsVisible);
        const changedOffer = visibleOffers.find(x => x.Id == Id);
        if (!changedOffer || !val) {
            return;
        }

        const intNewSort = parseInt(val);
        visibleOffers.sort((a, b) => {
            if (a.SortOrder == null && b.SortOrder != null) {
                return -1;
            }
            if (a.SortOrder != null && b.SortOrder == null) {
                return 1;
            }
            if (a.SortOrder == null && b.SortOrder == null) {
                return 0;
            }
            if (a.SortOrder != null && b.SortOrder != null && a.SortOrder > b.SortOrder) {
                return 1;
            }
            if (a.SortOrder != null && b.SortOrder != null && a.SortOrder < b.SortOrder) {
                return -1;
            }
            return 0;
        });

        const oldIndex = visibleOffers.indexOf(changedOffer);
        const newIndex = intNewSort - 1;
        let updatedOffers = arrayMoveImmutable(visibleOffers, oldIndex, newIndex);
        updatedOffers.forEach((_, index) => {
            const newSort = index + 1;
            updatedOffers[index].SortOrder = newSort;
            updateItemOfferCode(
                updatedOffers[index].Id,
                updatedOffers[index].IsVisible,
                updatedOffers[index].SortOrder
            );
        });
        forceUpdate();
    };

    const resetSortOrderAfterRemove = () => {
        // Sort and reassign
        const sortedOffers = [...clientVariablesWithValues];

        // Remove if not visible
        sortedOffers.forEach((_, index) => {
            if (!sortedOffers[index].IsVisible) {
                sortedOffers[index].SortOrder = null;
            }
        });

        // Resort
        sortedOffers.sort((a, b) => {
            if (a.SortOrder == null && b.SortOrder != null) {
                return -1;
            }
            if (a.SortOrder != null && b.SortOrder == null) {
                return 1;
            }
            if (a.SortOrder == null && b.SortOrder == null) {
                return 0;
            }
            if (a.SortOrder != null && b.SortOrder != null && a.SortOrder > b.SortOrder) {
                return 1;
            }
            if (a.SortOrder != null && b.SortOrder != null && a.SortOrder < b.SortOrder) {
                return -1;
            }
            return 0;
        });

        let newSort = 1;
        for (const offer of sortedOffers) {
            updateItemOfferCode(offer.Id, offer.IsVisible, offer.IsVisible ? newSort : null);
            const updateVariable = clientVariablesWithValues.find(x => x.Id == offer.Id);
            if (updateVariable) {
                updateVariable.SortOrder = updateVariable.IsVisible ? newSort : null;
            }

            if (offer.IsVisible) {
                newSort++;
            }
        }
    };

    const getNextSortOrder = () => Math.max(...clientVariablesWithValues.map(x => x.SortOrder || 0)) + 1;

    const updateClientVariable = (row: ClientVariableWithValue) => {
        const variableId = row.Id;
        const isVisible = row.IsVisible;
        let sortOrder: number | null = null;
        if (variableScope == "OfferCode") {
            if (isVisible && row.SortOrder == null) {
                sortOrder = getNextSortOrder();
                row.SortOrder = sortOrder;
            }
        }

        if (variableScope == "CampaignFlow") {
            // Set Empty Variable Value istead of null
            let variableValue = row.VariableValue;
            const variableKind: ClientVariableKind = row.VariableKind;
            if (!variableValue) {
                const newValueObject: VariableValueType = {
                    Kind: variableKind,
                    ValueString: null,
                    ValueDate: null,
                    FieldId: null,
                };
                variableValue = newValueObject;
            }
            dispatch(flowSetClientVariable(selectedFlow, variableId, variableValue, isVisible));
        } else if (variableScope == "OfferCode") {
            updateItemOfferCode(variableId, isVisible, sortOrder);
            // if removing need to reset sort
            if (!isVisible) {
                resetSortOrderAfterRemove();
            }
            invalidate();
        } else {
            return;
        }
    };

    const handleRequestSort = (_, property: string) => {
        if (order === "asc") {
            setOrder("desc");
            setOrderBy(property);
        } else {
            setOrder("asc");
            setOrderBy(property);
        }
    };

    const renderMaterialTable = () => {
        let tableData: Array<Array<Array<string | number | JSX.Element | null>>> = [];
        let tableHeaders = [
            ["IsVisible", "Is Visible", "smDown", "center"],
            ["SortOrder", "Order", "smDown"],
            ["VariableName", "Name", "mdDown"],
            ["VariableKind", "Type", "mdDown"],
        ];

        if (variableScope !== "OfferCode") {
            tableHeaders = [
                ["IsVisible", "Is Visible", "smDown", "center"],
                ["VariableName", "Name", "mdDown"],
                ["VariableKind", "Type", "mdDown"],
            ];
        }

        const emptyMessage = ["EmptyMessage", "No fields to display.", "smDown"];

        stableSort(clientVariablesWithValues, getSorting(order, orderBy)).map(dataRow => {
            if (variableScope == "OfferCode") {
                tableData.push([
                    ["IsVisible", checkboxCell(dataRow), "smDown", "center"],
                    ["SortOrder", numberInputCell(dataRow), "smDown"],
                    ["VariableName", labelCell(dataRow.VariableName), "mdDown"],
                    [
                        "VariableKind",
                        labelCell(dataRow.VariableSubType != null ? dataRow.VariableSubType : dataRow.VariableKind),
                        "mdDown",
                    ],
                ]);
            } else {
                tableData.push([
                    ["IsVisible", checkboxCell(dataRow), "smDown", "center"],
                    ["VariableName", labelCell(dataRow.VariableName), "mdDown"],
                    [
                        "VariableKind",
                        labelCell(dataRow.VariableSubType != null ? dataRow.VariableSubType : dataRow.VariableKind),
                        "mdDown",
                    ],
                ]);
            }
        });

        return (
            <Paper>
                <Table style={{ minWidth: "30vw" }}>
                    <TableHeader
                        headers={tableHeaders}
                        orderBy={orderBy}
                        order={order}
                        onClick={handleRequestSort}
                        widget={false}
                    />
                    {
                        <TableBody
                            data={tableData}
                            page={page}
                            rowsPerPage={rowsPerPage}
                            emptyMessage={emptyMessage}
                            widget={false}
                        />
                    }
                </Table>
                <TableFooter
                    displayDense={false}
                    displayPaging={true}
                    rowsPerPageOptions={[rowsPerPage]}
                    count={clientVariablesWithValues.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={(_, newPage) => {
                        setPage(newPage);
                    }}
                    onRowsPerPageChange={e => {
                        setRowsPerPage(parseInt(e.target.value, 10));
                        setPage(0);
                    }}
                />
            </Paper>
        );
    };

    return renderMaterialTable();
};

export default FlowVisibleVariablesEdit;
